import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Check, Receipt, Trash2, CircleDot } from "lucide-react";

const PHASES = [
  { key: "urgent", label: "Urgent", tone: "bg-red-50 text-red-700 border-red-200" },
  { key: "restorative", label: "Restorative", tone: "bg-blue-50 text-blue-700 border-blue-200" },
  { key: "maintenance", label: "Maintenance", tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
];

const peso = (n) => `₱${Number(n || 0).toLocaleString("en-PH", { minimumFractionDigits: 2 })}`;

export default function TreatmentPlanPanel({ patientId, meta, onCharted }) {
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [services, setServices] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // add-item form
  const [svcId, setSvcId] = useState("");
  const [tooth, setTooth] = useState("");
  const [surfaces, setSurfaces] = useState("");
  const [phase, setPhase] = useState("restorative");

  const load = async () => {
    try {
      const [p, s] = await Promise.all([
        api.get(`/dental/patients/${patientId}/treatment-plans`),
        api.get("/pos/services"),
      ]);
      setPlans(p.data || []);
      setActive((prev) => (p.data || []).find((x) => x.id === prev?.id) || (p.data || [])[0] || null);
      setServices((s.data || []).filter((x) => x.dental));
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [patientId]);

  const selectedService = useMemo(
    () => services.find((s) => s.id === svcId), [services, svcId]);

  const createPlan = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/dental/patients/${patientId}/treatment-plans`, {
        title: "Treatment Plan", status: "draft",
      });
      setPlans((p) => [data, ...p]);
      setActive(data);
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const addItem = async () => {
    if (!svcId) { toast.error("Pick a procedure"); return; }
    if (selectedService?.tooth_required && !tooth) {
      toast.error(`${selectedService.name} needs a tooth number`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/dental/treatment-plans/${active.id}/items`, {
        items: [{
          service_id: svcId,
          tooth: tooth.trim(),
          surfaces: surfaces.trim() ? surfaces.trim().toUpperCase().split("") : [],
          phase,
        }],
      });
      setActive(data);
      setPlans((p) => p.map((x) => (x.id === data.id ? data : x)));
      setTooth(""); setSurfaces("");
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const completeItem = async (itemId) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/dental/treatment-plans/${active.id}/items/${itemId}/complete`);
      setActive(data);
      toast.success("Marked done and charted");
      if (onCharted) onCharted();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const removeItem = async (itemId) => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/dental/treatment-plans/${active.id}/items/${itemId}`);
      setActive(data);
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const bill = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/dental/treatment-plans/${active.id}/bill`, {});
      toast.success(`${data.billed_items} item(s) billed — ${data.transaction_number}`);
      nav(`/pos/transactions/${data.transaction_id}`);
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="text-sm text-slate-500 p-4">Loading plans…</div>;

  if (!plans.length) {
    return (
      <Card className="p-6 text-center">
        <p className="text-sm text-slate-600 mb-3">No treatment plan yet for this patient.</p>
        <Button onClick={createPlan} disabled={busy} className="bg-blue-600 hover:bg-blue-700">
          <Plus size={14} className="mr-1" /> Start a treatment plan
        </Button>
      </Card>
    );
  }

  const items = active?.items || [];
  const billable = items.filter((i) => i.status === "completed" && !i.pos_transaction_id);

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="flex items-center gap-2">
            <select
              value={active?.id || ""}
              onChange={(e) => setActive(plans.find((p) => p.id === e.target.value))}
              className="h-9 rounded-md border border-slate-300 px-2 text-sm"
              data-testid="plan-select"
            >
              {plans.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title} · {p.status} · {peso(p.estimated_total)}
                </option>
              ))}
            </select>
            <Button size="sm" variant="outline" onClick={createPlan} disabled={busy}>
              <Plus size={14} className="mr-1" /> New
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-600">
              Estimate <b className="text-slate-900">{peso(active?.estimated_total)}</b>
            </span>
            <Button
              size="sm"
              onClick={bill}
              disabled={busy || !billable.length}
              className="bg-emerald-600 hover:bg-emerald-700"
              title={billable.length ? "" : "Mark work as done first"}
              data-testid="btn-bill-plan"
            >
              <Receipt size={14} className="mr-1" />
              Bill {billable.length ? `(${billable.length})` : ""}
            </Button>
          </div>
        </div>

        {/* Add item */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2 p-3 bg-slate-50 rounded-md">
          <select
            value={svcId}
            onChange={(e) => setSvcId(e.target.value)}
            className="md:col-span-2 h-9 rounded-md border border-slate-300 px-2 text-sm"
            data-testid="plan-service"
          >
            <option value="">Procedure…</option>
            {services.map((s) => (
              <option key={s.id} value={s.id}>{s.name} — {peso(s.default_price)}</option>
            ))}
          </select>
          <Input
            placeholder={selectedService?.tooth_required ? "Tooth (required)" : "Tooth"}
            value={tooth}
            onChange={(e) => setTooth(e.target.value)}
            className="h-9"
            data-testid="plan-tooth"
          />
          <Input
            placeholder="Surfaces e.g. MO"
            value={surfaces}
            onChange={(e) => setSurfaces(e.target.value)}
            className="h-9"
          />
          <div className="flex gap-2">
            <select
              value={phase}
              onChange={(e) => setPhase(e.target.value)}
              className="h-9 rounded-md border border-slate-300 px-2 text-sm flex-1"
            >
              {PHASES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
            <Button size="sm" onClick={addItem} disabled={busy} data-testid="btn-add-plan-item">
              <Plus size={14} />
            </Button>
          </div>
        </div>
      </Card>

      {PHASES.map((ph) => {
        const rows = items.filter((i) => i.phase === ph.key && i.status !== "cancelled");
        if (!rows.length) return null;
        return (
          <Card key={ph.key} className="p-4" data-testid={`phase-${ph.key}`}>
            <div className={`inline-block text-xs font-semibold px-2 py-0.5 rounded border mb-2 ${ph.tone}`}>
              {ph.label}
            </div>
            <div className="divide-y divide-slate-100">
              {rows.map((i) => (
                <div key={i.id} className="py-2 flex items-center gap-3" data-testid={`plan-item-${i.id}`}>
                  {i.status === "completed"
                    ? <Check size={16} className="text-emerald-600 shrink-0" />
                    : <CircleDot size={16} className="text-slate-300 shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-slate-900 truncate">
                      {i.service_name_snapshot}
                      {i.tooth ? <b> · #{i.tooth}{i.surfaces?.length ? ` ${i.surfaces.join("")}` : ""}</b> : null}
                    </div>
                    <div className="text-[11px] text-slate-400">
                      {peso(i.unit_price)}{Number(i.quantity) !== 1 ? ` × ${i.quantity}` : ""}
                      {i.pos_transaction_id ? " · billed" : ""}
                      {i.status === "completed" && !i.pos_transaction_id ? " · ready to bill" : ""}
                    </div>
                  </div>
                  {i.status !== "completed" ? (
                    <Button size="sm" variant="outline" onClick={() => completeItem(i.id)} disabled={busy}
                            data-testid={`btn-complete-${i.id}`}>
                      <Check size={13} className="mr-1" /> Done
                    </Button>
                  ) : null}
                  {!i.pos_transaction_id && i.status !== "completed" ? (
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => removeItem(i.id)} disabled={busy}>
                      <Trash2 size={13} />
                    </Button>
                  ) : null}
                </div>
              ))}
            </div>
          </Card>
        );
      })}

      {items.length === 0 ? (
        <div className="text-sm text-slate-500 px-1">
          No items yet — add procedures above. Marking one <b>Done</b> also charts it on the odontogram.
        </div>
      ) : null}
    </div>
  );
}
